import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { TranslationService } from '@/layout/service/translation.service';
import { WorkingDayRow, weekdayNames } from '@/cloud/models/working-hours.models';

/** The rows with one row changed. */
export function updateRow(rows: WorkingDayRow[], index: number, change: Partial<WorkingDayRow>): WorkingDayRow[] {
    return rows.map((r, i) => (i === index ? { ...r, ...change } : r));
}

/**
 * Opening hours per weekday, Monday first: a checkbox per day and its opening and closing time. Used for the tenant's
 * working hours and for a location's override.
 */
@Component({
    selector: 'app-working-days-editor',
    standalone: true,
    imports: [FormsModule, CheckboxModule, InputTextModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div class="working-days" role="group" [attr.aria-label]="label()">
            @for (row of rows(); track row.day; let i = $index) {
                <div class="working-day">
                    <p-checkbox [inputId]="idPrefix() + '-day-' + row.day" [binary]="true" [ngModel]="row.open" (ngModelChange)="change(i, { open: $event })" [disabled]="disabled()" />
                    <label class="working-day__name" [for]="idPrefix() + '-day-' + row.day">{{ dayNames()[row.day] }}</label>
                    @if (row.open) {
                        <input
                            pInputText
                            type="time"
                            class="working-day__time working-day__time--from"
                            [attr.aria-label]="dayNames()[row.day] + ' ' + t()('workingHours.from')"
                            [ngModel]="row.start"
                            (ngModelChange)="change(i, { start: $event })"
                            [disabled]="disabled()"
                        />
                        <span class="text-color-secondary" aria-hidden="true">–</span>
                        <input
                            pInputText
                            type="time"
                            class="working-day__time working-day__time--to"
                            [attr.aria-label]="dayNames()[row.day] + ' ' + t()('workingHours.to')"
                            [ngModel]="row.end"
                            (ngModelChange)="change(i, { end: $event })"
                            [disabled]="disabled()"
                        />
                    } @else {
                        <span class="working-day__closed text-color-secondary text-sm">{{ t()('workingHours.closed') }}</span>
                    }
                </div>
            }
        </div>
    `,
    styles: [
        `
            :host {
                display: block;
            }
            .working-days {
                display: grid;
                gap: 0.5rem;
            }
            .working-day {
                display: grid;
                grid-template-columns: auto 6rem auto auto auto;
                align-items: center;
                justify-content: start;
                column-gap: 0.75rem;
                min-height: 2.5rem;
            }
            .working-day__name {
                font-weight: 500;
            }
            .working-day__time {
                width: 9.5rem;
            }
            /* Phones: day name on one line, its hours below. */
            @media (max-width: 30rem) {
                .working-day {
                    grid-template-columns: auto minmax(0, 1fr) auto minmax(0, 1fr);
                    row-gap: 0.375rem;
                }
                .working-day__name {
                    grid-column: 2 / 3;
                    grid-row: 1;
                }
                .working-day__closed {
                    grid-column: 3 / -1;
                    grid-row: 1;
                    justify-self: end;
                }
                .working-day__time {
                    width: 100%;
                }
                .working-day__time--from {
                    grid-column: 1 / 3;
                }
            }
        `
    ]
})
export class WorkingDaysEditorComponent {
    private translation = inject(TranslationService);
    readonly t = this.translation.t;

    readonly rows = input.required<WorkingDayRow[]>();
    readonly disabled = input(false);
    /** Prefix for element ids, unique per editor on the page. */
    readonly idPrefix = input('wh');
    readonly label = input('');
    readonly rowsChange = output<WorkingDayRow[]>();

    readonly dayNames = computed(() => weekdayNames(this.translation.currentLanguage(), 'long'));

    change(index: number, change: Partial<WorkingDayRow>): void {
        this.rowsChange.emit(updateRow(this.rows(), index, change));
    }
}
